import { useId, useState, type KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { useModal } from '../../../contexts/ModalContext';
import { ApiError } from '../../../lib/apiClient';
import { setMailboxMembers, updateMailbox } from '../../../lib/mailAdminApi';
import type {
  MailAdminCandidateDto,
  MailAdminMailboxDto,
  MailAdminMemberDto,
} from '../../../lib/mailAdminTypes';
import { countOwners, validateAlias } from '../../../lib/mailAdminValidation';
import { StatusBadge } from '../../StatusBadge';
import { AdminDialog } from './AdminDialog';
import { MailboxMembersEditor } from './MailboxMembersEditor';

const errMessage = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const sameMembers = (a: readonly MailAdminMemberDto[], b: readonly MailAdminMemberDto[]) =>
  a.length === b.length && a.every((m, i) => m.uid === b[i].uid && m.role === b[i].role);

const sectionTitle = { margin: '1.25rem 0 0.5rem', fontSize: '0.85rem', fontWeight: 600 } as const;

export function MailboxDetailDialog({
  mailbox: initial,
  candidates,
  onClose,
  onChanged,
}: {
  mailbox: MailAdminMailboxDto;
  candidates: readonly MailAdminCandidateDto[];
  onClose: () => void;
  /** Called with the server's copy after every successful save so the list can refresh. */
  onChanged: (mailbox: MailAdminMailboxDto) => void;
}) {
  const { confirm } = useModal();
  const nameId = useId();
  const aliasId = useId();

  const [mailbox, setMailbox] = useState(initial);
  const [displayName, setDisplayName] = useState(initial.displayName);
  const [aliases, setAliases] = useState(initial.aliases);
  const [aliasInput, setAliasInput] = useState('');
  const [members, setMembers] = useState(initial.members);
  const [busy, setBusy] = useState<null | 'details' | 'status' | 'members'>(null);
  const [errors, setErrors] = useState<{ details?: string; status?: string; members?: string }>({});
  const [saved, setSaved] = useState<string | null>(null);

  const personal = mailbox.type === 'personal';
  const disabledMailbox = mailbox.status === 'disabled';

  const apply = (next: MailAdminMailboxDto) => {
    setMailbox(next);
    setDisplayName(next.displayName);
    setAliases(next.aliases);
    setMembers(next.members);
    onChanged(next);
  };

  // --- name + aliases -----------------------------------------------------
  const aliasValue = aliasInput.trim();
  const aliasError = aliasValue
    ? validateAlias(aliasValue, { primary: mailbox.address, existing: aliases })
    : null;

  const addAlias = () => {
    if (!aliasValue || aliasError) return;
    setAliases((prev) => [...prev, aliasValue]);
    setAliasInput('');
  };
  const onAliasKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addAlias();
    }
  };

  const nameTrimmed = displayName.trim();
  const detailsDirty = nameTrimmed !== mailbox.displayName || !sameList(aliases, mailbox.aliases);
  const detailsValid = nameTrimmed.length > 0;

  const saveDetails = async () => {
    if (!detailsDirty || !detailsValid || busy) return;
    setBusy('details');
    setSaved(null);
    setErrors((e) => ({ ...e, details: undefined }));
    try {
      const next = await updateMailbox(mailbox.id, {
        ...(nameTrimmed !== mailbox.displayName ? { displayName: nameTrimmed } : {}),
        ...(!sameList(aliases, mailbox.aliases) ? { aliases } : {}),
      });
      apply(next);
      setSaved('Details saved.');
    } catch (err) {
      setErrors((e) => ({ ...e, details: errMessage(err, 'Could not save the changes.') }));
    } finally {
      setBusy(null);
    }
  };

  // --- status -------------------------------------------------------------
  const toggleStatus = async () => {
    if (busy) return;
    const next = disabledMailbox ? 'active' : 'disabled';
    if (next === 'disabled') {
      const ok = await confirm({
        title: 'Disable this mailbox?',
        message:
          'A disabled mailbox stops receiving mail: messages sent to it, and to its aliases, will not be delivered to anyone. Existing mail stays stored. You can enable it again at any time.',
        confirmLabel: 'Disable mailbox',
        danger: true,
      });
      if (!ok) return;
    }
    setBusy('status');
    setSaved(null);
    setErrors((e) => ({ ...e, status: undefined }));
    try {
      apply(await updateMailbox(mailbox.id, { status: next }));
      setSaved(next === 'disabled' ? 'Mailbox disabled.' : 'Mailbox enabled.');
    } catch (err) {
      setErrors((e) => ({ ...e, status: errMessage(err, 'Could not change the status.') }));
    } finally {
      setBusy(null);
    }
  };

  // --- members ------------------------------------------------------------
  const ownersLeft = countOwners(members);
  const membersDirty = !personal && !sameMembers(members, mailbox.members);
  const membersValid = ownersLeft >= 1;

  const saveMembers = async () => {
    if (!membersDirty || !membersValid || busy) return;
    setBusy('members');
    setSaved(null);
    setErrors((e) => ({ ...e, members: undefined }));
    try {
      apply(await setMailboxMembers(mailbox.id, members.map((m) => ({ uid: m.uid, role: m.role }))));
      setSaved('Members saved.');
    } catch (err) {
      setErrors((e) => ({ ...e, members: errMessage(err, 'Could not save the members.') }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminDialog
      wide
      title={mailbox.address}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
        <StatusBadge label={personal ? 'Personal' : 'Shared'} tone="neutral" />
        <StatusBadge label={disabledMailbox ? 'Disabled' : 'Active'} tone={disabledMailbox ? 'warn' : 'good'} />
      </div>
      <div role="status" aria-live="polite" className="form-hint" style={{ minHeight: '1rem' }}>
        {saved}
      </div>

      <div className="form-card" style={{ boxShadow: 'none', padding: 0, margin: 0, maxWidth: 'none' }}>
        <h3 style={sectionTitle}>Details</h3>
        <label htmlFor={nameId}>Display name</label>
        <input id={nameId} value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="off" />

        <label htmlFor={aliasId}>Aliases</label>
        {aliases.length === 0 ? (
          <div className="form-hint">No aliases.</div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {aliases.map((a) => (
              <li key={a} className="label" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                {a}
                <button
                  type="button"
                  aria-label={`Remove alias ${a}`}
                  onClick={() => setAliases((prev) => prev.filter((x) => x !== a))}
                  style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', color: 'inherit', display: 'inline-flex' }}
                >
                  <X size={11} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <input
            id={aliasId}
            value={aliasInput}
            placeholder="name@mediclynk.com"
            autoComplete="off"
            spellCheck={false}
            aria-invalid={!!aliasError}
            aria-describedby={`${aliasId}-msg`}
            onChange={(e) => setAliasInput(e.target.value)}
            onKeyDown={onAliasKey}
            style={{ flex: '1 1 12rem', minWidth: 0 }}
          />
          <button type="button" className="btn btn-secondary" disabled={!aliasValue || !!aliasError} onClick={addAlias}>
            Add alias
          </button>
        </div>
        <div id={`${aliasId}-msg`} className="form-hint" aria-live="polite">
          {aliasError ?? 'Lowercase, on mediclynk.com or mediclynkos.com.'}
        </div>
        {errors.details && (
          <div className="error-banner" role="alert" style={{ marginBottom: 0 }}>
            {errors.details}
          </div>
        )}
        <div>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!detailsDirty || !detailsValid || busy !== null}
            onClick={() => void saveDetails()}
          >
            {busy === 'details' ? 'Saving…' : 'Save details'}
          </button>
        </div>

        <h3 style={sectionTitle}>Members</h3>
        <MailboxMembersEditor
          members={members}
          candidates={candidates}
          personal={personal}
          disabled={busy !== null}
          onChange={setMembers}
        />
        {!membersValid && (
          <div className="form-hint" role="alert">
            A mailbox needs at least one owner.
          </div>
        )}
        {errors.members && (
          <div className="error-banner" role="alert" style={{ marginBottom: 0 }}>
            {errors.members}
          </div>
        )}
        {!personal && (
          <div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!membersDirty || !membersValid || busy !== null}
              onClick={() => void saveMembers()}
            >
              {busy === 'members' ? 'Saving…' : 'Save members'}
            </button>
          </div>
        )}

        <h3 style={sectionTitle}>Status</h3>
        <div className="form-hint">
          {disabledMailbox
            ? 'This mailbox is disabled and is not receiving mail.'
            : 'Disabling stops this mailbox from receiving mail.'}
        </div>
        {errors.status && (
          <div className="error-banner" role="alert" style={{ marginBottom: 0 }}>
            {errors.status}
          </div>
        )}
        <div>
          <button
            type="button"
            className={disabledMailbox ? 'btn btn-primary' : 'btn btn-danger'}
            disabled={busy !== null}
            onClick={() => void toggleStatus()}
          >
            {busy === 'status' ? 'Working…' : disabledMailbox ? 'Enable mailbox' : 'Disable mailbox'}
          </button>
        </div>
      </div>
    </AdminDialog>
  );
}
